// Timestamp dividers for the continuous-chat timeline (BIZY-638).
//
// This module owns only the *decision* of where a divider belongs — not its
// label. Not every message gets one; we only break between bursts:
//   • the start of each new calendar day
//   • within *today*, larger gaps (default ≥ 1h) between messages — today's
//     messages can break more often.
// Rendering (localized) is left to the caller, which passes the returned
// timestamp to the shared <RelativeTime> component so copy stays translated.
//
// Hydrated assistant/proactive turns carry no timestamp (see `hydrateMessages`),
// so those messages are skipped; the gap is measured between the timestamps we
// do have (i.e. user turns).

export const DEFAULT_TIMESTAMP_GAP_MS = 60 * 60 * 1000; // 1 hour

/** Minimal message shape needed for divider computation (both ChatMessage and BizyChatMessage satisfy it). */
export interface TimestampedMessage {
	timestamp?: Date | string;
}

export interface GetMessageDividersOptions {
	/** "Now" reference; injectable for tests. Defaults to a fresh Date. */
	now?: Date;
	/** Minimum same-day gap (ms) that triggers a time divider. Defaults to 1h. */
	gapMs?: number;
}

function toDate(raw: Date | string): Date {
	return raw instanceof Date ? raw : new Date(raw);
}

function isSameDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Compute which message indices should be preceded by a timestamp divider.
 * Returns a Map keyed by message index whose value is the timestamp to render
 * (via a localized component such as <RelativeTime>).
 */
export function getMessageDividers(messages: ReadonlyArray<TimestampedMessage>, options: GetMessageDividersOptions = {}): Map<number, Date> {
	const { now = new Date(), gapMs = DEFAULT_TIMESTAMP_GAP_MS } = options;
	const dividers = new Map<number, Date>();
	let prevTs: Date | null = null;

	for (let i = 0; i < messages.length; i++) {
		const rawTs = messages[i].timestamp;
		if (!rawTs) continue;
		const ts = toDate(rawTs);
		if (Number.isNaN(ts.getTime())) continue;

		let showDivider = false;
		if (prevTs === null) {
			// First timestamped message anchors the top of the transcript.
			showDivider = true;
		} else if (!isSameDay(ts, prevTs)) {
			// Day rolled over — always mark the new day.
			showDivider = true;
		} else if (isSameDay(ts, now) && ts.getTime() - prevTs.getTime() >= gapMs) {
			// Same day (today) with a big gap — break the burst.
			showDivider = true;
		}

		if (showDivider) dividers.set(i, ts);
		prevTs = ts;
	}

	return dividers;
}
